import { toLocale } from '@zuruflex/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';

/**
 * Sign in with Google — our button, Google's popup.
 *
 * This used to call `google.accounts.id.renderButton`, which draws Google's
 * own widget in an iframe. The trouble is that widget decides for itself to
 * show the *personalized* variant — "Sign in as Kwame", with the face and
 * the address of whoever the browser last signed in as — whenever it
 * recognises a returning session. There is no flag to turn that off: it
 * degrades to the plain button only at `size: medium`, `type: icon`, or
 * under 200px wide, none of which survive a full-width 40px pill. So it
 * sat under the yellow primary looking like a component borrowed from
 * another product, and leaked one person's address onto a shared screen.
 *
 * What replaces it is an ordinary `<Button variant="outline">` — the same
 * pill, the same height, the same focus ring as everything else on the
 * form — wired to Google's OAuth *token* flow. The click opens Google's
 * account chooser, we get an access token back, and we read the profile
 * from Google's userinfo endpoint with it.
 *
 * The credential that reaches `onCredential` is therefore an access token
 * rather than an ID token. `GoogleService` on the API takes either: the
 * phones still sign in natively and still hand over an ID token, and that
 * path is untouched.
 */
const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** Profile and email, which is all we ask for and all the API reads. */
const SCOPE = 'openid email profile';

const USERINFO_URL = 'https://openidconnect.googleapis.com/v1/userinfo';

interface TokenResponse {
  access_token?: string;
  error?: string;
}

interface TokenClient {
  requestAccessToken: () => void;
}

interface GoogleIdentityServices {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: TokenResponse) => void;
        error_callback?: (error: { type?: string }) => void;
      }) => TokenClient;
    };
  };
}

declare global {
  var google: GoogleIdentityServices | undefined;
}

let loader: Promise<void> | undefined;

function loadGsi(): Promise<void> {
  if (globalThis.google?.accounts?.oauth2) return Promise.resolve();
  loader ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Google script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google script failed to load'));
    document.head.append(script);
  });
  return loader;
}

/**
 * What Google tells us about the person, read from its userinfo endpoint.
 *
 * Only ever used to fill a form in front of them: the access token is
 * posted to the API, which checks with Google who it was minted for before
 * believing a word of this.
 */
export interface GoogleProfile {
  email?: string;
  /** Google's display name, e.g. "Kwame Mensah". */
  name?: string;
  givenName?: string;
  familyName?: string;
  avatarUrl?: string;
}

async function fetchProfile(accessToken: string): Promise<GoogleProfile> {
  try {
    const response = await fetch(USERINFO_URL, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return {};
    const claims = (await response.json()) as Record<string, unknown>;

    const text = (key: string) => (typeof claims[key] === 'string' ? claims[key] : undefined);
    return {
      email: text('email'),
      name: text('name'),
      givenName: text('given_name'),
      familyName: text('family_name'),
      avatarUrl: text('picture'),
    };
  } catch {
    /*
     * An empty profile is survivable — the form simply starts blank, and
     * the API reads the real profile itself from the same endpoint. A
     * thrown error here would instead lose a credential the person has
     * already consented to.
     */
    return {};
  }
}

export interface GoogleButtonProps {
  /** Blank renders nothing at all, which is how the feature is turned off. */
  clientId: string;
  /**
   * Receives Google's access token, to be posted to the API for
   * verification, along with the profile read with it — enough to fill a
   * sign-up form without asking for what Google has already been told.
   */
  onCredential: (accessToken: string, profile: GoogleProfile) => void;
  /** `signup_with` on a sign-up form, `signin_with` elsewhere. */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  /**
   * `outline` is the white button, which sits under the yellow primary
   * without competing with it. `filled` matches the ink chrome where the
   * surrounding surface is dark.
   */
  theme?: 'outline' | 'filled';
  /**
   * The language to draw the label in. Narrowed to one Zuruflex actually
   * speaks, so callers can hand over `i18n.language` as it comes.
   */
  locale?: string;
  className?: string;
}

const LABEL_KEY = {
  signin_with: 'action.googleSignIn',
  signup_with: 'action.googleSignUp',
  continue_with: 'action.googleContinue',
} as const;

export function GoogleButton({
  clientId,
  onCredential,
  text = 'signin_with',
  theme = 'outline',
  locale,
  className,
}: GoogleButtonProps) {
  const { t } = useTranslation('common');
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const client = useRef<TokenClient | null>(null);

  // Read through a ref so re-rendering the parent does not rebuild the
  // token client, which would drop a popup already in flight.
  const callback = useRef(onCredential);
  callback.current = onCredential;

  /**
   * Built once, on mount rather than on click. `requestAccessToken` opens a
   * popup, and a popup only survives the blocker when it is opened inside
   * the click itself — there is no room to await a script load first.
   */
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    void loadGsi()
      .then(() => {
        if (cancelled || !globalThis.google) return;

        client.current = globalThis.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: SCOPE,
          callback: (response) => {
            // `error` covers the person closing the chooser or declining,
            // neither of which is a failure worth showing them.
            if (!response.access_token || response.error) {
              setBusy(false);
              return;
            }
            const token = response.access_token;
            void fetchProfile(token).then((profile) => {
              setBusy(false);
              callback.current(token, profile);
            });
          },
          // Fires when the popup itself never opened — blocked, or closed
          // before Google could answer. Without it the button stays busy.
          error_callback: () => setBusy(false),
        });
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const start = useCallback(() => {
    if (!client.current) return;
    setBusy(true);
    client.current.requestAccessToken();
  }, []);

  if (!clientId || failed) return null;

  return (
    <Button
      type="button"
      variant={theme === 'filled' ? 'secondary' : 'outline'}
      // Disabled only until the script lands, which is usually before the
      // form is readable. The height is the Button default, so nothing
      // moves when it becomes live.
      disabled={!ready || busy}
      isLoading={busy}
      onClick={start}
      className={cn('w-full', className)}
    >
      {busy ? null : <GoogleMark />}
      {t(LABEL_KEY[text], { lng: toLocale(locale) })}
    </Button>
  );
}

/**
 * Google's four-colour mark, which their branding guidelines require be
 * used unaltered and at no less than 18px beside an 18px label — hence the
 * explicit size, overriding the 16px `[&_svg]:size-4` the button applies to
 * the lucide icons everywhere else.
 */
function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" className="!size-[18px]" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
      />
    </svg>
  );
}
